import { useEffect } from 'preact/hooks';

/** The attribute the highlight is driven by. Set on <body>, read by targets via a group variant. */
export const GLOW_ATTRIBUTE = 'data-tour-glow';
/** The attribute a highlightable element identifies itself with. */
export const TARGET_ATTRIBUTE = 'data-tour-target';

interface TourSpotlightSlotProps {
  target: string;
  scrollIntoView?: boolean;
}

/**
 * Headless: highlights the element(s) named by `target`.
 *
 * The attribute goes on <body>, not on the target, and the target styles itself with a Tailwind
 * `group-data-` variant. That inversion is what makes this reliable: the habitat's controls
 * mount asynchronously (the zoom bar appears once the iframe loads, the camera selector once the
 * camera list arrives), so anything that wrote the attribute onto the element would decorate
 * nothing and need a MutationObserver to catch up. A CSS rule simply matches whenever the
 * element exists — and several elements may share one target name, which is how the capture step
 * lights the photo, clip and react buttons together.
 *
 * <body> is already used this way (`group/mobile-layout`, see ProductsPromptModal).
 */
const TourSpotlightSlot = ({ target, scrollIntoView = false }: TourSpotlightSlotProps) => {
  useEffect(() => {
    const { body } = document;
    body.setAttribute(GLOW_ATTRIBUTE, target);
    // The named group the target's `group-data-[...]/tour:` variant resolves against.
    body.classList.add('group/tour');

    return () => {
      // Only clear if we still own it: a fast step change can mount the next slot before this
      // cleanup runs, and blindly removing would wipe the incoming target's attribute.
      if (body.getAttribute(GLOW_ATTRIBUTE) === target) body.removeAttribute(GLOW_ATTRIBUTE);
    };
  }, [target]);

  useEffect(() => {
    if (!scrollIntoView) return;
    const element = document.querySelector(`[${TARGET_ATTRIBUTE}="${target}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [target, scrollIntoView]);

  return null;
};

export default TourSpotlightSlot;
